/**
 * core/i18n.ts · 国际化 (ZH/VI/EN)
 *
 * 复用 v200.16.1 zh.json + vi.json (7093 keys 一致):
 *   - 不再 TN_AUTO_I18N DOM 拦截 (v154 IIFE 模式)
 *   - 改用 i18next + t('key') 显式调用
 *   - 1042 处硬编码 ZH 必须替换 (团队 W3 任务)
 *
 * 团队 W3 补:
 *   - EN 翻译 (扩 SEA TOP 3 国际客户)
 *   - 日期/数字 Intl 本地化
 */

import i18next from 'i18next';

const DEFAULT_LANG = 'zh';
const SUPPORTED = ['zh', 'vi', 'en'] as const;
export type Lang = (typeof SUPPORTED)[number];

export async function initI18n(): Promise<void> {
  const lang = getStoredLang() || detectLang();

  // 并行 fetch zh.json + vi.json (复用 v200.16.1 public/i18n/)
  const [zh, vi] = await Promise.all([
    fetch('/i18n/zh.json').then((r) => r.json()).catch(() => ({})),
    fetch('/i18n/vi.json').then((r) => r.json()).catch(() => ({})),
  ]);

  await i18next.init({
    lng: lang,
    fallbackLng: 'zh',
    resources: {
      zh: { translation: zh },
      vi: { translation: vi },
      en: { translation: {} }, // 团队 W3 补 en.json
    },
    interpolation: { escapeValue: false },
  });

  document.documentElement.lang = lang === 'zh' ? 'zh-CN' : lang === 'vi' ? 'vi-VN' : 'en';
}

/**
 * 翻译 · 业务代码主要 API
 *
 * 用法:
 *   t('home.gm.title') → '总经理驾驶舱'
 *   t('home.gm.title', { lng: 'vi' }) → 'Bảng điều khiển GM'
 */
export function t(key: string, opts?: Record<string, unknown>): string {
  return i18next.t(key, opts) as string;
}

export function getCurrentLang(): Lang {
  const l = i18next.language as Lang;
  return SUPPORTED.includes(l) ? l : DEFAULT_LANG;
}

export async function setLang(lang: Lang): Promise<void> {
  await i18next.changeLanguage(lang);
  localStorage.setItem('tn-lang', lang);
  document.documentElement.lang = lang === 'zh' ? 'zh-CN' : lang === 'vi' ? 'vi-VN' : 'en';
  document.dispatchEvent(new CustomEvent('tn-lang-change', { detail: lang }));
}

// ============================================================
// 内部
// ============================================================

function getStoredLang(): Lang | null {
  const v = localStorage.getItem('tn-lang');
  if (v && (SUPPORTED as readonly string[]).includes(v)) return v as Lang;
  return null;
}

function detectLang(): Lang {
  const nav = navigator.language.toLowerCase();
  if (nav.startsWith('vi')) return 'vi';
  if (nav.startsWith('zh')) return 'zh';
  if (nav.startsWith('en')) return 'en';
  return DEFAULT_LANG;
}

/**
 * VND 格式化 helper (Trungnam 主货币)
 *
 * 100000000 → 'đ100.0M'
 */
export function vnd(amount: number): string {
  const v = amount || 0;
  if (v >= 1e9) return 'đ' + (v / 1e9).toFixed(1) + 'B';
  if (v >= 1e6) return 'đ' + (v / 1e6).toFixed(1) + 'M';
  if (v >= 1e3) return 'đ' + (v / 1e3).toFixed(0) + 'K';
  return 'đ' + v.toFixed(0);
}
